import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ListTodo } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import type { PropsAcaoNoRegistro } from '../../nucleo/modulos-web';
import { get } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, SeletorQuadro } from './comum';
import { FormCard } from './FormCard';
import type { Quadro } from './tipos';

/**
 * Fase 2 (W23, K11): "Criar tarefa" no chatter de qualquer registro. Abre o formulário de card do Tarefas (o mesmo do
 * "Novo card" do kanban) com o vínculo ao registro: entidade, id, título e a rota da tela, para o card mostrar o link de
 * volta. Só com o Tarefas instalado (o chatter confere) e a permissão de criar card; o servidor confere que quem cria vê o
 * registro. A aprovação do card continua só humana (as colunas de aprovação seguem as regras da W10).
 */
export function BotaoCriarTarefa({ entidade, registroId, titulo }: PropsAcaoNoRegistro) {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [quadroId, setQuadroId] = useState<string | null>(null);
  // Os quadros só são lidos quando o formulário abre (a mesma chave do kanban, então o cache é o mesmo).
  const quadros = useQuery({ queryKey: [...CHAVE, 'quadros'], queryFn: () => get<{ itens: Quadro[] }>('/tarefas/quadros'), enabled: aberto });
  // O card do próprio Tarefas não cria tarefa de si mesmo.
  if (!s.pode('tarefas.criar') || entidade.startsWith('tarefas.')) return null;
  const lista = quadros.data?.itens ?? [];
  const quadro = lista.find((q) => q.id === quadroId) ?? lista.find((q) => q.codigo === 'demandas') ?? lista[0];

  return (
    <>
      <Botao variante="fantasma" tamanho="sm" data-criar-tarefa title={t('registro.criarTarefaAjuda')} onClick={() => setAberto(true)} icone={<ListTodo className="size-4" />}>
        {t('registro.criarTarefa')}
      </Botao>
      <Drawer
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        largura="lg"
        titulo={t('registro.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-card" disabled={!quadro}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {aberto && (
          <div className="flex flex-col gap-4">
            {lista.length > 1 && quadro && <SeletorQuadro quadros={lista} valor={quadro.id} aoMudar={setQuadroId} />}
            {quadros.isSuccess && !quadro && <p className="text-sm text-tinta-3">{t('registro.semQuadro')}</p>}
            {quadro && (
              <FormCard
                key={quadro.id}
                quadro={quadro}
                usuarioId={s.usuario.id}
                vinculo={{ entidade, id: registroId, titulo: titulo?.trim() || null, link: `${location.pathname}${location.search}`.slice(0, 300) }}
                aoSalvar={async () => {
                  setAberto(false);
                  toast.sucesso(t('registro.criada'));
                  await qc.invalidateQueries({ queryKey: CHAVE });
                }}
              />
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}
